/**
 * Filename sanitization for files uploaded to Firebase Storage. The seller-
 * provided File.name is attacker-controllable and can carry path traversal
 * segments (`../`), control characters, hostile Unicode (BiDi overrides,
 * zero-width joiners), or pathological lengths. We never trust it for either
 * the storage path or the persisted `name` field.
 *
 * Hardening is defence in depth — Firebase Storage treats paths as opaque
 * keys (so `..` isn't directory-traversable the way it is on a filesystem)
 * and /api/download already rejects out-of-prefix paths and `..` segments.
 * But the same name flows through Content-Disposition headers, download
 * dialogs rendered in the buyer's browser, and seller dashboards. Normalizing
 * once at upload removes a category of small footguns at the source.
 */

/** Max base (pre-extension) length. Generous enough for legitimate
 *  descriptive names, tight enough that a 500-char hostile name can't bloat
 *  Firestore docs or break header parsing. */
const MAX_BASE_LEN = 80;
/** Max extension length. Real extensions are 2–4 chars; the cap is for
 *  hostile inputs like a megabyte-long suffix after the last dot. */
const MAX_EXT_LEN = 16;

/**
 * Strip a user-supplied filename to a safe ASCII subset and bounded length.
 * - Allowed character set: [A-Za-z0-9._-]. Everything else becomes `_`.
 * - Runs of dots collapse to one (kills `../`, `...`, accidental `..` in name).
 * - Leading dot/dash/underscore stripped (no hidden-file or flag-lookalike names).
 * - Base capped to MAX_BASE_LEN, extension to MAX_EXT_LEN (alphanumeric only).
 * - Empty result falls back to `file`.
 */
export function sanitizeUploadFilename(name: string): string {
  const lastDot = name.lastIndexOf('.');
  const hasExt = lastDot > 0 && lastDot < name.length - 1;
  const rawBase = hasExt ? name.slice(0, lastDot) : name;
  const rawExt = hasExt ? name.slice(lastDot + 1) : '';

  const cleanBase = (rawBase
    .replace(/[^A-Za-z0-9._-]/g, '_')
    .replace(/\.+/g, '.')
    .replace(/^[._-]+/, '')
    .slice(0, MAX_BASE_LEN)) || 'file';

  const cleanExt = rawExt
    .replace(/[^A-Za-z0-9]/g, '')
    .slice(0, MAX_EXT_LEN);

  return cleanExt ? `${cleanBase}.${cleanExt}` : cleanBase;
}

/**
 * Reserve a unique name within the given `taken` set, suffixing `-1`, `-2`, …
 * before the extension on collision. Mutates `taken` to include the chosen
 * name. Use when multiple sanitized filenames could collide within the same
 * storage folder (e.g. two gallery uploads named `IMG (1).png` and
 * `IMG_(1).png` both collapse to `IMG_1_.png`).
 */
export function reserveUniqueFilename(taken: Set<string>, name: string): string {
  if (!taken.has(name)) {
    taken.add(name);
    return name;
  }
  const lastDot = name.lastIndexOf('.');
  const hasExt = lastDot > 0 && lastDot < name.length - 1;
  const base = hasExt ? name.slice(0, lastDot) : name;
  const ext = hasExt ? name.slice(lastDot) : '';
  let i = 1;
  while (taken.has(`${base}-${i}${ext}`)) i++;
  const result = `${base}-${i}${ext}`;
  taken.add(result);
  return result;
}
